import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "./context/AppContext";
import { BUSINESS } from "./lib/business";
import { buildLocalBusinessSchema } from "./lib/schema";
import { FloatingContact } from "./components/landing/FloatingContact";
import "./App.css";

// Lazy-loaded routes — the visitor only downloads the JS for the page they
// actually open (8 separate chunks instead of one 800KB bundle). This is the
// single biggest perf win for first paint on slow mobile connections.
const Home = lazy(() => import("./pages/Home"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ReservationPage = lazy(() => import("./pages/ReservationPage"));
const LaMaison = lazy(() => import("./pages/LaMaison"));
const FleetPage = lazy(() => import("./pages/FleetPage"));
const Admin = lazy(() => import("./pages/Admin"));
const CGV = lazy(() => import("./pages/CGV"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));

// Minimal fallback shown while a lazy route is loading. Plain "Riviera-night"
// background + a thin gold ring so it never feels broken — just elegant.
const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: "#0a0a0c" }}
    aria-label="Chargement"
  >
    <span
      className="w-8 h-8 rounded-full border-2 border-transparent animate-spin"
      style={{ borderTopColor: "#d4af37", borderRightColor: "#d4af37" }}
    />
  </div>
);

const ScrollManager = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

const AnalyticsTracker = () => {
  // SPA-aware GA4 page_view tracker. The base gtag script lives in
  // public/index.html with send_page_view:false, so we fire one explicit
  // page_view per React Router navigation here — that way every route
  // change shows up in GA4 with the correct path/title.
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
};

const SiteSchema = () => {
  // Inject the site-wide LocalBusiness JSON-LD once at app mount. It is
  // marked data-schema-id="site" so that the useSEO hook never clears it
  // when navigating between routes.
  useEffect(() => {
    const id = 'script[data-schema-id="site"]';
    if (document.head.querySelector(id)) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-schema-id", "site");
    script.textContent = JSON.stringify(buildLocalBusinessSchema());
    document.head.appendChild(script);
  }, []);
  return null;
};

const SiteFloatingWhatsApp = () => null;

// Global floating contact — hidden on the reservation page (form already
// present) and on the admin panel. Everywhere else, two discreet round
// buttons (WhatsApp + mail) stay bottom-right for one-tap access.
const GlobalFloatingContact = () => {
  const { pathname } = useLocation();
  const HIDDEN_PATHS = ["/reservation", "/admin"];
  if (HIDDEN_PATHS.includes(pathname)) return null;
  return <FloatingContact />;
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <SiteSchema />
        <ScrollManager />
        <AnalyticsTracker />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/reservation" element={<ReservationPage />} />
            <Route path="/la-maison" element={<LaMaison />} />
            <Route path="/flotte" element={<FleetPage />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </Suspense>
        <GlobalFloatingContact />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AppProvider>
  );
}

export default App;
